import { addressHasChanged } from '../../util/address';
import { imagesHaveChanged } from '../imageAdmin/util';
import { hasDocuments } from '../validation/animal';

export const getOrgValidationErrors = org => {
  if (!org) return { org: 'data is required' };
  const errors = {};
  if (!org.name) {
    errors.name = 'name is required';
  }
  if (org.herdIds) {
    if (!Array.isArray(org.herdIds)) {
      errors.herdIds = 'herdIds should be an array';
    } else {
      org.herdIds.forEach(herdId => {
        if (typeof herdId !== 'string') {
          errors.herdIds = `herdId is not a string`;
        } else {
          if (!herdId.match(/^[0-9]{6}$/)) {
            errors.herdIds =
              'Herd ID should be a 6 digit number (with no letters)';
          }
        }
      });
    }
  }
  if (org.breeds) {
    if (!Array.isArray(org.breeds)) {
      errors.breeds = 'breeds should be an array';
    } else {
      org.breeds.forEach(breed => {
        if (typeof breed !== 'string') {
          errors.breeds = `breed is not a string`;
        }
      });
    }
  }
  if (org.websiteURL) {
    try {
      const url = new URL(org.websiteURL);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        errors.websiteURL = 'website URL must be an http or https URL';
      }
    } catch (error) {
      errors.websiteURL = 'website URL must be a valid URL';
    }
  }
  if (org.logo) {
    try {
      const url = new URL(org.logo);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        errors.logo = 'logo must be an http or https URL';
      }
    } catch (error) {
      errors.logo = 'logo must be a valid URL';
    }
  }
  if (org.memberFarm) {
    if (!org.address || (!org.address.town && !org.address.county)) {
      errors.address_town = 'town (or county) is required for a member farm';
      errors.address_county = 'county (or town) is required for a member farm';
    }
    if (!org.breeds) {
      errors.breeds = 'breeds are required for a member farm';
    }
    if (!org.farmerName) {
      errors.farmerName = 'farmer name is required for a member farm';
    }
    if (!org.description) {
      errors.description = 'description is required for a member farm';
    }
  }
  return errors;
};

export const isValidOrg = org => {
  if (!org) return false;
  const errors = getOrgValidationErrors(org);
  return Object.values(errors).length < 1;
};

export const documentsHavechanged = (docs1, docs2) => {
  try {
    if (!docs1 !== !docs2) return true;
    if (docs1) {
      if (typeof docs1 === 'object') {
        const keys = Object.keys(docs1);
        for (let i = 0; i < keys.length; i++) {
          if (docs1[keys[i]] !== docs2[keys[i]]) return true;
        }
        if (keys.length !== Object.keys(docs2).length) return true;
      } else if (docs1 !== docs2) {
        return true;
      }
    }
    return false;
  } catch (error) {
    return true;
  }
};

export const scoresHaveChanged = (scores1, scores2, scoreDefinitions) => {
  if (!scores1 && !!scores2) return true;
  if (!scores2 && !!scores1) return true;
  if (!scores1 && !scores2) return false;
  const keys = Object.keys(scoreDefinitions);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (scores1[key] !== scores2[key]) return true;
  }
  return false;
};

export const herdIdsHaveChanged = (herdIds1, herdIds2) => {
  return ArrayHasChanged(herdIds1, herdIds2);
};

export const breedsHaveChanged = (breeds1, breeds2) => {
  return ArrayHasChanged(breeds1, breeds2);
};

const ArrayHasChanged = (arr1, arr2) => {
  if (!!arr1 !== !!arr2) return true;
  if (arr1) {
    if (Array.isArray(arr1)) {
      if (!Array.isArray(arr2)) return true;
      if (arr1.length !== arr2.length) return true;
      for (let i = 0; i < arr1.length; i++) {
        if (!arr2.includes(arr1[i])) return true;
      }
    } else if (Array.isArray(arr2)) return true;
  }
  return false;
};

const standardFields = [
  'name', 'farmerName', 'breeds', 'herdSize', 'farmSize', 'logo',
  'foundingFarm', 'memberFarm', 'websiteURL', 'id', 'description'
];

export const standardFieldsHaveChanged = (org1, org2) => {
  const fields = standardFields;
  for (let i = 0; i < fields.length; i++) {
    if (org1[fields[i]] !== org2[fields[i]]) return true;
  }
  return false;
};

export const newAddressFields = address => {
  if (!address) return false;
  const attrs = [
    'addressLine1',
    'addressLine2',
    'addressLine3',
    'town',
    'county',
    'postCode'
  ];
  for (let i = 0; i < attrs.length; i++) {
    if (address[attrs[i]]) return true;
  }
  return false;
};

export const hasScore = scores => {
  if (!scores) return false;
  const orgScores = [
    'welfare',
    'pastureFed',
    'health',
    'croppingGhgReduction',
    'soilCondition',
    'traceElements',
    'plantVarieties',
    'pastureVsGrain',
    'waterQuality',
    'pgiStatus',
    'methaneMitigation',
    'fertility',
    'calvingEase'
  ];
  for (let i = 0; i < orgScores.length; i++) {
    if (scores[orgScores[i]]) return true;
  }
  return false;
};

export const newOrgHasChanged = org => {
  if (!org) return false;
  if (org.name) return true;
  if (org.farmerName) return true;
  if (org.users && Array.isArray(org.users) && org.users.length) return true;
  if (hasDocuments(org.documents)) return true;
  if (newAddressFields(org.address)) return true;
  if (org.images && Array.isArray(org.images) && org.images.length) return true;
  if (org.videoData && org.videoData.base) return true;
  if (hasScore(org)) return true;
  if (org.herdIds) return true;
  if (org.breeds && Array.isArray(org.breeds) && org.breeds.length) return true;
  if (typeof org.herdSize === 'number') return true;
  if (typeof org.farmSize === 'number') return true;
  if (org.logo) return true;
  if (org.foundingFarm) return true;
  if (org.memberFarm) return true;
  if (org.websiteURL) return true;
  if (org.description) return true;
  return false;
};

export const orgHasChanged = (org1, org2, scoreDefinitions) => {
  if (!org1 && !!org2) return true;
  if (!org2 && !!org1) return true;
  if (!org1 && !org2) return false;
  if (standardFieldsHaveChanged(org1, org2)) return true;
  if (documentsHavechanged(org1.documents, org2.documents)) return true;
  if (herdIdsHaveChanged(org1.herdIds, org2.herdIds)) return true;
  if (addressHasChanged(org1.address, org2.address)) return true;
  if (imagesHaveChanged(org1.images, org2.images)) return true;
  if (scoresHaveChanged(
    org1.scores, org2.scores, scoreDefinitions
  )) return true;
  return false;
};
